/**
 * useInputValueManager - Custom hook for managing input values with typing detection
 *
 * Handles the common pattern of:
 * - Tracking pending values during typing
 * - Applying changes immediately for spinner/keyboard/wheel events
 * - Applying pending changes on blur
 *
 * @param {Function} applyValueChange - Function to call when value should be applied to store
 * @returns {Object} - { handleChange, handleBlur, displayValue, isTyping }
 */

import { useState, useCallback } from 'react'

// Helper to clamp a value between min and max
const clamp = (value, min, max) => {
  if (min !== undefined && value < min) return min
  if (max !== undefined && value > max) return max
  return value
}

export const useInputValueManager = (baseValue, applyValueChange, min, max) => {
  const [pendingValue, setPendingValue] = useState(null)
  const [isTyping, setIsTyping] = useState(false)

  // Display value - use pending value during typing, otherwise base value
  const displayValue =
    isTyping && pendingValue !== null ? pendingValue : baseValue

  const handleChange = useCallback(
    (value, evt) => {
      if (value !== null && value !== undefined && !isNaN(value)) {
        switch (evt?.type) {
          case 'change':
            // User is typing - store pending value but don't apply to store
            setPendingValue(value)
            setIsTyping(true)
            break
          case 'click':
          case 'keydown':
          case 'wheel':
            // Spinner click, arrow key, or mouse wheel - apply immediately
            applyValueChange(clamp(value, min, max))
            setPendingValue(null)
            setIsTyping(false)
            break
          default:
            // Default case - apply immediately
            applyValueChange(clamp(value, min, max))
            setPendingValue(null)
            setIsTyping(false)
            break
        }
      }
    },
    [applyValueChange, min, max],
  )

  const handleBlur = useCallback(() => {
    // On blur, apply any pending value changes
    if (isTyping && pendingValue !== null) {
      applyValueChange(clamp(pendingValue, min, max))
    }
    setPendingValue(null)
    setIsTyping(false)
  }, [isTyping, pendingValue, applyValueChange, min, max])

  return {
    handleChange,
    handleBlur,
    displayValue,
    isTyping,
    pendingValue,
  }
}
